import { Button, ButtonProps } from '@dtx-company/design-system/src/components/Button/Button'
import { Dispatch, FC, SetStateAction, useState } from 'react'
import { ExportFormat } from '@dtx-company/flow-codegen/src/code/generated.types'
import { ListItemIcon, MenuItem } from '@mui/material'
import { Menu } from '@dtx-company/design-system/src'
import { Stack } from '@dtx-company/design-system/src/components/Stack/Stack'
import { Typography } from '@dtx-company/design-system/src/components/Typography/Typography'
import { useIsMobile } from '@dtx-company/true-common/src/hooks/useIsMobile'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'

const DH_EXPORT_FORMATS = [
  {
    label: '.JPG',
    value: ExportFormat.Jpg,
    description: 'Best for sharing quickly',
    icon: <ImageOutlinedIcon />
  },
  {
    label: '.PDF',
    value: ExportFormat.Pdf,
    description: 'Best for printing and emailing',
    icon: <InsertDriveFileOutlinedIcon />
  },
  {
    label: '.PNG',
    value: ExportFormat.Png,
    description: 'Best for images and illustrations',
    icon: <ImageOutlinedIcon />
  }
]

export interface DownloadTemplateButtonProps {
  onDownloadClick: () => void
  loading: boolean
  disabled: boolean
  selectedExportFormat: ExportFormat
  setExportFormat: Dispatch<SetStateAction<ExportFormat>>
  extraButtonProps?: ButtonProps
}

export const DownloadTemplateButton: FC<DownloadTemplateButtonProps> = props => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const isMobile = useIsMobile()
  const open = Boolean(anchorEl)

  return (
    <Stack direction="row" spacing={0.5}>
      <Button
        color="secondary"
        fullWidth
        startIcon={DownloadIcon}
        onClick={props.onDownloadClick}
        loading={props.loading}
        disabled={props.disabled}
        {...(props.extraButtonProps ?? {})}
      >
        Download
      </Button>
      <Button
        disabled={props.disabled || props.loading}
        endIcon={ArrowDropDownIcon}
        color={'secondary'}
        onClick={e => {
          setAnchorEl(e.currentTarget)
        }}
        {...(props.extraButtonProps ?? {})}
      >
        {props.selectedExportFormat.toUpperCase()}
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: isMobile ? 'top' : 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: isMobile ? 'bottom' : 'top',
          horizontal: 'right'
        }}
      >
        {DH_EXPORT_FORMATS.map(exportFormat => {
          return (
            <MenuItem
              key={exportFormat.label}
              onClick={() => {
                props.setExportFormat(exportFormat.value)
                setAnchorEl(null)
              }}
              selected={exportFormat.value === props.selectedExportFormat}
            >
              <ListItemIcon
                sx={theme => ({
                  color: theme.brand.ink
                })}
              >
                {exportFormat.icon}
              </ListItemIcon>
              <Stack direction="column">
                <Typography variant="labelLarge">{exportFormat.label}</Typography>
                <Typography variant="bodySmall">{exportFormat.description}</Typography>
              </Stack>
            </MenuItem>
          )
        })}
      </Menu>
    </Stack>
  )
}
